import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "@/components/Reveal";

const CHARGING_IMG = "/assets/truck-charging.jpg";

const CHAPTERS = [
  {
    num: "01",
    title: "A New Class of Carrier",
    body: "Electrohaul Pvt. Ltd. is a next-generation sustainable trucking company transforming freight through clean, intelligent, sustainable mobility — cutting logistics costs and carbon emissions without compromising reliability or operational efficiency.",
  },
  {
    num: "02",
    title: "Intelligence on Every Kilometre",
    body: "Advanced electric vehicles fused with smart fleet management, real-time telematics, optimized route planning and dependable charging infrastructure. Complete visibility into fleet performance, energy consumption and delivery status — so customers decide with data, not guesswork.",
  },
  {
    num: "03",
    title: "Sustainability at the Core",
    body: "Every electric kilometre driven reduces greenhouse gas emissions, noise pollution and fossil-fuel dependence. The future of logistics is not just electric — it is connected, data-driven and customer-centric.",
  },
];

export default function Manifesto() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section id="company" data-testid="manifesto-section" className="relative py-32">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <Reveal>
          <Overline data-testid="manifesto-overline">The Manifesto</Overline>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-semibold tracking-tighter sm:text-5xl lg:text-6xl">
            Freight rewritten <span className="text-volt">in Electrons.</span>
          </h2>
        </Reveal>

        <div className="mt-20">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.08}>
              <div
                data-testid={`manifesto-chapter-${c.num}`}
                className="group grid grid-cols-1 gap-6 border-t border-line py-14 transition-colors duration-500 hover:bg-ash/60 md:grid-cols-12 md:gap-8"
              >
                <h3 className="font-display text-2xl font-medium tracking-tight sm:text-3xl md:col-span-4 md:pl-4">
                  {c.title}
                </h3>
                <p className="max-w-xl text-sm leading-relaxed text-smoke md:col-span-8 md:pr-4 md:text-base">
                  {c.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div ref={imgRef} className="mt-8 overflow-hidden">
        <motion.div style={{ y }} className="spotlight relative h-[46vh] md:h-[60vh]">
          <img
            src={CHARGING_IMG}
            alt="ElectroHaul electric truck charging beside wind turbines"
            data-testid="manifesto-charging-image"
            className="h-[120%] w-full scale-110 object-cover"
          />
          <div className="absolute inset-0 z-10 flex items-end bg-gradient-to-t from-coal via-transparent to-transparent">
            <p className="px-6 pb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-volt md:px-12">
              Renewable corridors. Dependable charging. Every kilometre electric.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
